/**
 * 高德地图插件 - 环境配置与常量
 */
import type { SVGProps, ComponentType } from 'react'
import { ENV } from '@/config/env'
// 图片
import MapStartSvg from '@/assets/imgs/order-center/map-start.svg'
import MapEndSvg from '@/assets/imgs/order-center/map-end.svg'

const DEFAULT_AMAP_JS_VERSION = '2.0'

let isLocal = false;
if (/localhost|^192\.168|^172\.30/.test(window.location.hostname)) {
  isLocal = true;
}

/**
 * 高德地图key
 * localhost 访问配置用：momo私人key: 6afab6f2cbe1a06dfa2e5559afc0ef5b，只用于本地开发使用
 * hosts 127.0.0.1 pc-pre.huolala.cn 访问配置用：6afab6f2cbe1a06dfa2e5559afc0ef5b, 需开启https访问。不然定位无法使用
 */
const amapKey = isLocal ? 'acc507cec0b84ccfd2ae4d1f4e74ba98' : 'acc507cec0b84ccfd2ae4d1f4e74ba98';

export const DEFAULT_CENTER_POINT = {
  lon: 116.397428,
  lat: 39.900539
}

const getRuntimeConfig = () => {
  const envKeyMap = {
    dev: amapKey,
    stg: amapKey,
    pre: amapKey,
    prod: amapKey,
  } as const

  return {
    amapKey: envKeyMap[ENV],
  }
}

export const getAmapKey = () => getRuntimeConfig().amapKey

export const getAmapScriptUrl = (key: string) => {
  const params = new URLSearchParams({
    v: DEFAULT_AMAP_JS_VERSION,
    key,
    // MoveAnimation 提供 Marker.moveAlong（配对中随机小车回放依赖）
    // Geolocation：同城开城等城市定位（order-center-same-city-shipping-city）
    plugin: 'AMap.ToolBar,AMap.MoveAnimation,AMap.Geolocation',
  })
  return `https://webapi.amap.com/maps?${params.toString()}`
}

/**
 * 固定地图图标：起点/终点使用本地 SVG React 组件（与旧百度地图视觉一致），
 * 司机车辆使用高德 CDN 图标（可按视觉需求替换为 map-motor.svg）。
 * 使用方：overlays.syncRouteMarkers 的 getIcon 回调返回此处的组件或 URL 字符串。
 */
export const AMAP_FIXED_MARKER_ICONS = {
  /** 起点图标，46×48 SVG React 组件 */
  start: MapStartSvg as ComponentType<SVGProps<SVGSVGElement>>,
  /** 终点图标，46×48 SVG React 组件 */
  end: MapEndSvg as ComponentType<SVGProps<SVGSVGElement>>,
  /** 初始化地图点位占位图标 */
  initMapPlaceholder: 'https://static.huolala.cn/image/d63a73bf6fd592bf2503bd1876d229d049206837.png',
}

/**
 * 装/卸 SVG（46×48）锚点偏移：使尖端小圆（viewBox 内约 y=38）对齐经纬度，而非画布底边 y=48。
 * 高德 content Marker 默认左上角为锚点，offset 为 Pixel(dx, dy) 平移。
 * 可按视觉在 start/end 上分别微调（单位：px）。
 */
export const AMAP_LOAD_UNLOAD_MARKER_OFFSET = {
  /** 装 */
  start: [-23, -38] as [number, number],
  /** 卸 */
  end: [-23, -38] as [number, number],
}

/** 路线轮询最小间隔（30s），与业务约定的司机位置更新频率对齐 */
export const DEFAULT_ROUTE_POLLING_INTERVAL = 30 * 1000

/**
 * 用户手动拖动/缩放地图后，在该时间窗口内不因覆盖物更新而自动 setFitView / fitBounds（毫秒）。
 * 到期后下一次数据同步会再次自动适配视野。
 */
export const MAP_VIEWPORT_USER_INTERACTION_LOCK_MS = 3 * 60 * 1000


/** 路线折线样式 */
export const ROUTE_STYLE = {
  strokeWidth: 5,
  strokeColor: '#3377FF',
  outlineWidth: 1,
  outlineColor: '#3061F2',
  strokeAndOutlineOpacity: 1,
  isOutline: true,
}
